import { Component, OnInit, Input, ViewChild, EventEmitter, Output, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { SwiperOptions } from 'swiper';
import { SwiperComponent } from "swiper/angular";
import { ApiV1Job } from '@bluecrew/crew-member-backend-angular-client';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'app-position-schedules-carousel',
  templateUrl: './position-schedules-carousel.component.html',
  styleUrls: ['./position-schedules-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class PositionSchedulesCarouselComponent implements OnInit, AfterViewInit {
  @ViewChild('swiper', { static: false }) swiper?: SwiperComponent;
  readonly APP_IS_ON_DESKTOP: boolean = this.platform.is('desktop');

  @Input() jobs: ApiV1Job[];
  @Input() blueshifts: ApiV1Job[];
  @Input() startJobId: string;
  @Input() backgroundColor?: string = '#853434';
  @Input() enableSlides: boolean = true;
  @Output() jobSlideIndexEvent = new EventEmitter<number>();

  enablePrevBtn: boolean = false;
  enableNextBtn: boolean = false;
  numSlides: number;
  swiperClickedIndex: number;
  jobIdToCardIndex: Map<string, number> = new Map<string, number>();
  blueshiftJobId = 'blueshift';

  carouselConfig: SwiperOptions = {
    grabCursor: true,
    observer: true,
    observeParents: true,
    slideClass: 'position-schedules-carousel-slide',
    slidesOffsetBefore: 12,
    slidesOffsetAfter: 12,
    slidesPerView: 'auto',
    slideToClickedSlide: true,
    spaceBetween: 12,
  };

  constructor(
    private platform: Platform,
    private cd: ChangeDetectorRef
    ) { }

  ngOnInit() {
    this.numSlides = this.hasBlueshiftSlide() ? this.jobs.length + 1 : this.jobs.length;
    if (this.hasBlueshiftSlide()) {
      this.jobIdToCardIndex.set(this.blueshiftJobId, 0);
    }
    for (const [i, job] of this.jobs.entries()){
      if (this.hasBlueshiftSlide()) {
        this.jobIdToCardIndex.set(job.legacyExternalJobId, i + 1);
      } else {
        this.jobIdToCardIndex.set(job.legacyExternalJobId, i);
      }
    }
    if (this.APP_IS_ON_DESKTOP) {
      if (this.carouselFitsScreen()) {
        this.carouselConfig.slideToClickedSlide = false;
        this.carouselConfig.allowTouchMove = false;
      }
    }
  }

  ngAfterViewInit() {
    if (this.APP_IS_ON_DESKTOP) {
      this.checkFirstOrLastSlide()
    }
    this.swiperClickedIndex = this.jobIdToCardIndex.get(this.startJobId);
    this.goToJobSlide(this.swiperClickedIndex);
  }

  ngOnChanges() {
    this.numSlides = this.hasBlueshiftSlide() ? this.jobs.length + 1 : this.jobs.length;
  }

  goToJobSlide(jobSlideIndex: number) {
    if (!this.carouselFitsScreen() || !this.APP_IS_ON_DESKTOP) {
      this.swiper.swiperRef.slideTo(jobSlideIndex, undefined, false);
    }
    this.jobSlideChangeEvent();
  }

  jobSlideChangeEvent() {
    this.swiperClickedIndex = this.swiper.swiperRef.clickedIndex || this.swiperClickedIndex;
    if (this.swiperClickedIndex >= this.numSlides) {
      this.swiperClickedIndex = this.numSlides - 1;
    }
    this.checkFirstOrLastSlide();
    this.jobSlideIndexEvent.emit(this.swiperClickedIndex);
  }

  nextSlide() {
    this.swiper.swiperRef.slideNext();
  }

  prevSlide() {
    this.swiper.swiperRef.slidePrev();
  }

  checkFirstOrLastSlide() {
    if (this.APP_IS_ON_DESKTOP) {
      if (this.carouselFitsScreen()) {
        this.enablePrevBtn = false;
        this.enableNextBtn = false;
      } else {
        if (this.swiper.swiperRef.isBeginning || this.swiper.swiperRef.clickedIndex === 0 ||
            this.swiper.swiperRef.activeIndex === 0) {
          this.enablePrevBtn = false;
          this.enableNextBtn = true;
        } else if (this.swiper.swiperRef.isEnd || this.swiper.swiperRef.clickedIndex >= this.jobs.length - 2 ||
                    this.swiper.swiperRef.activeIndex >= this.jobs.length - 2) {
          this.enablePrevBtn = true;
          this.enableNextBtn = false;
        } else {
          this.enablePrevBtn = true;
          this.enableNextBtn = true;
        }
      }
    }
    this.cd.detectChanges();
  }

  shouldScheduleDetailBeHighlighted(cardIndex: number) {
    return (cardIndex == this.swiperClickedIndex || (cardIndex == 0 && this.swiperClickedIndex == undefined));
  }

  handleClick(swiper) {
    // Swiper registers two clicks sometimes and the second one is undefined. Bug? CREW-755
    if (swiper.clickedIndex !== undefined) {
      this.swiperClickedIndex = swiper.clickedIndex
      this.goToJobSlide(swiper.clickedIndex)
    }
  }

  carouselFitsScreen() {
    return this.numSlides <= 2;
  }

  hasBlueshiftSlide(): boolean {
    return this.blueshifts && this.blueshifts.length > 0;
  }
}
